import { useContext, useState } from 'react';
import produce from 'immer';
import useAsyncEffect from 'src/hooks/useAsyncEffect';
import {
  getAlerts,
  getCompetitorFulfillers,
  getInsertionDates,
  getClientRegions,
  getCompetitorSellers,
  getCompetitorSites,
  getSourceOwner,
  AlertItem,
  InsertionDate,
  OptionGroup,
} from '../services/alertResults.service';
import { useAppStateContext } from 'src/stateProviders/useAppStateContext';
import { SourceOwnersItem, CompetitorSitesItem } from '../api/alertResults.api';
import { AlertResultsContext } from '../AlertResultsProvider';

export type AlertResultsFiltersState = {
  alerts: AlertItem[];
  sourceSites: SourceOwnersItem[];
  insertionDates: InsertionDate[];
  clientRegions: OptionGroup[];
  competitorFulfillers: string[];
  competitorSellers: string[];
  competitorSites: CompetitorSitesItem[];
};

const initialState: AlertResultsFiltersState = {
  alerts: [],
  sourceSites: [],
  insertionDates: [],
  clientRegions: [],
  competitorSellers: [],
  competitorFulfillers: [],
  competitorSites: [],
};

const useAlertResultsFiltersFetch = () => {
  const { user } = useAppStateContext();
  const [state, setState] = useState<AlertResultsFiltersState>(initialState);
  const { headerFilters, changeHeaderFilters, clearSidebarFilters } = useContext(AlertResultsContext);

  const currentAlertId = headerFilters.alertId;
  const currentSourceSiteId = headerFilters.sourceSiteId;
  const currentInsertionDate = headerFilters.insertionDate;

  const changeFilters = <T extends keyof AlertResultsFiltersState>(
    value: AlertResultsFiltersState[T],
    filterKey: T
  ) => {
    setState(
      produce((draft: AlertResultsFiltersState) => {
        draft[filterKey] = value;
      })
    );
  };

  useAsyncEffect(async () => {
    if (user) {
      const alerts = await getAlerts(user.userEmail);
      changeFilters(alerts, 'alerts');
    }
  }, [user?.userEmail]);

  useAsyncEffect(async () => {
    changeHeaderFilters('-1', 'sourceSiteId');
    clearSidebarFilters();

    if (currentAlertId) {
      const sourceOwner = await getSourceOwner({ alertId: currentAlertId });
      changeFilters(sourceOwner, 'sourceSites');
    }
  }, [currentAlertId]);

  useAsyncEffect(async () => {
    clearSidebarFilters();

    if (currentAlertId && currentSourceSiteId) {
      const insertionDates = await getInsertionDates({
        alertId: currentAlertId,
        sourceOwnerId: currentSourceSiteId,
      });
      const firstDate = insertionDates[0]?.value ?? null;
      const dateByValue = insertionDates.find(({ value }) => value === currentInsertionDate);
      const selectedDate = dateByValue || insertionDates.find(({ id }) => id === currentInsertionDate);

      changeFilters(insertionDates, 'insertionDates');
      changeHeaderFilters(selectedDate?.value ?? firstDate, 'insertionDate');
    }
  }, [currentAlertId, currentSourceSiteId]);

  useAsyncEffect(async () => {
    clearSidebarFilters();

    if (currentAlertId && currentSourceSiteId && currentInsertionDate) {
      const request = {
        alertId: currentAlertId,
        sourceOwnerId: currentSourceSiteId,
        alertDate: currentInsertionDate,
      };
      const [regions, sites, sellers, fulfillers] = await Promise.all([
        getClientRegions(request),
        getCompetitorSites(request),
        getCompetitorSellers(request),
        getCompetitorFulfillers(request),
      ]);
      changeFilters(regions, 'clientRegions');
      changeFilters(sites, 'competitorSites');
      changeFilters(sellers, 'competitorSellers');
      changeFilters(fulfillers, 'competitorFulfillers');
    }
  }, [currentInsertionDate]);

  return state;
};

export default useAlertResultsFiltersFetch;
